import "../step.js";
import "../callout.js";
import "../mixins/appointment_slot_picker.js";
import "./visit/fields/age_range_field.js";
import "./visit/fields/visit_type_rules_field.js";
import "./visit/fields/returning_patient_field.js";
import "./visit/fields/visit_type_field.js";
import "./visit/fields/slot_field.js";
import "./visit/callouts/not_licensed_for_location_callout.js";
import "./visit/callouts/provider_type_callout.js";

window.AppointmentBooking ??= {};
window.AppointmentBooking.Wizard ??= {};
window.AppointmentBooking.Wizard.Views ??= {};

// Preserve sub-view registrations (e.g. ReturningPatientField) that
// depth-first imports already placed on the VisitStep namespace object.
const __VisitStepPrior = AppointmentBooking.Wizard.Views.VisitStep;

AppointmentBooking.Wizard.Views.VisitStep = class VisitStep extends (
  AppointmentBooking.Wizard.Views.Mixins.AppointmentSlotPicker(AppointmentBooking.Wizard.Views.Step)
) {
  constructor() {
    super(...arguments);
    this.isDisabled = this.isDisabled.bind(this);
    this.areRequirementsValid = this.areRequirementsValid.bind(this);
    this.initialize = this.initialize.bind(this);
    this.handleReturningPatientChange = this.handleReturningPatientChange.bind(this);
    this.handleVisitTypeIdChange = this.handleVisitTypeIdChange.bind(this);
    this.updateSuggesterIfConditionsChanged = this.updateSuggesterIfConditionsChanged.bind(this);
    this.render = this.render.bind(this);
    this.renderSuggester = this.renderSuggester.bind(this);
    this.whenSlotSuggesterLoaded = this.whenSlotSuggesterLoaded.bind(this);
    this.initializeSlotSuggester = this.initializeSlotSuggester.bind(this);
    this.getPageTitle = this.getPageTitle.bind(this);
    this.resetSelectedVisitTypeRule = this.resetSelectedVisitTypeRule.bind(this);
    this.setSlotFromCurrentVisitType = this.setSlotFromCurrentVisitType.bind(this);
    this.ageRangeRestriction = this.ageRangeRestriction.bind(this);
    this.visitTypeRuleRestriction = this.visitTypeRuleRestriction.bind(this);
    this.shouldShowSlotSuggester = this.shouldShowSlotSuggester.bind(this);
    this.slotSelected = this.slotSelected.bind(this);
    this.shouldGatherLocationDuringVisit = this.shouldGatherLocationDuringVisit.bind(this);
    this.licenseRestriction = this.licenseRestriction.bind(this);
    this.selectedVisitTypeChecksProviderLicense = this.selectedVisitTypeChecksProviderLicense.bind(this);
    this.getLicensedLocations = this.getLicensedLocations.bind(this);
  }

  isDisabled() {
    return this.model.get("decision_tree_enabled");
  }

  areRequirementsValid() {
    return (
      this.slotSelected() &&
      this.model.get("visit_type") &&
      !this.ageRangeRestriction() &&
      !this.visitTypeRuleRestriction() &&
      !this.licenseRestriction()
    );
  }

  initialize() {
    super.initialize(...arguments);

    this.returningPatientField = new AppointmentBooking.Wizard.Views.VisitStep.ReturningPatientField({
      model: this.model,
      condition: () => this.model.get("visit_types").length,
    });

    this.visitTypeField = new AppointmentBooking.Wizard.Views.VisitStep.VisitTypeField({
      model: this.model,
      condition: () => this.model.validVisitTypes().length,
    });

    this.ageRangeField = new AppointmentBooking.Wizard.Views.VisitStep.AgeRangeField({
      model: this.model,
      condition: () => {
        const visitType = this.model.get("visit_type");

        if (!visitType) {
          return false;
        }

        return visitType.verify_age_range;
      },
    });

    this.ageRangeCallout = new AppointmentBooking.Wizard.Views.Callout({
      model: this.model,
      className: "borderless",
      template: "messages/age_range",
      condition: () => this.ageRangeRestriction(),
      analytics: "message-shown",
    });

    this.visitTypeRulesField = new AppointmentBooking.Wizard.Views.VisitStep.VisitTypeRulesField({
      model: this.model,
      condition: () => {
        const visitType = this.model.get("visit_type");

        if (!visitType) {
          return false;
        }

        return visitType.rules.length;
      },
    });

    this.slotMessagesCallout = new AppointmentBooking.Wizard.Views.Callout({
      parent: this,
      model: this.model,
      className: "borderless",
      template: "step/visit/callouts/slot_messages",
      condition: () => this.slotSelected() && this.model.get("slot_messages").length,
    });

    this.noMatchingVisitTypeCallout = new AppointmentBooking.Wizard.Views.Callout({
      model: this.model,
      className: "borderless",
      template: "step/visit/callouts/no_matching_visit_type",
      condition: () => !this.model.get("visit_type") && this.model.get("last_valid_visit_type"),
      analytics: "message-shown",
    });

    this.slotField = new AppointmentBooking.Wizard.Views.VisitStep.SlotField({
      model: this.model,
      condition: () =>
        !this.ageRangeRestriction() && !this.visitTypeRuleRestriction() && !this.shouldShowSlotSuggester(),
    });

    this.locationDuringVisitField = new AppointmentBooking.Wizard.Views.Field({
      model: this.model,
      template: "step/visit/fields/location_during_visit",
      condition: () => this.shouldGatherLocationDuringVisit(),
    });

    this.notLicensedForLocationCallout = new AppointmentBooking.Wizard.Views.VisitStep.NotLicensedForLocationCallout({
      model: this.model,
      className: "borderless",
      condition: () => this.model.get("location_during_visit") && this.licenseRestriction(),
      analytics: "message-shown",
    });

    this.providerTypeCallout = new AppointmentBooking.Wizard.Views.VisitStep.ProviderTypeCallout({
      model: this.model,
      className: "borderless",
      analytics: "message-shown",
      condition: () => !_.isEmpty(this.model.get("provider_type_message")),
    });

    this.listenToIfEnabled(this.model, "change:returning_patient", this.handleReturningPatientChange.bind(this));
    this.listenToIfEnabled(this.model, "change:visit_type_id", this.handleVisitTypeIdChange.bind(this));
    this.listenToIfRenderable(this.model, "change", this.updateSuggesterIfConditionsChanged.bind(this)); // Update the suggester once on change instead of adding listeners for each attribute's change event
    return this.whenSlotSuggesterLoaded((suggester) =>
      this.listenToIfEnabled(suggester.model, "change:selectedSlotId", (_, value) =>
        this.model.set("ehr_appointment_slot_id", value),
      ),
    ); // Update the wizard with the selected suggested slot
  }

  // EVENT HANDLERS

  handleReturningPatientChange() {
    let last_valid_visit_type_id;
    let visitType;
    if (this.model.isVisitTypeValid()) {
      return;
    }

    // If we don't have an exact match, find a visit type with the same match name
    // Else if we stored a visit type before a transition to an invalid state, use that visit type if it is now valid
    const currentVisitType = this.model.get("visit_type");
    if (currentVisitType) {
      visitType = this.model.findValidVisitType({ match_name: currentVisitType.match_name });
    } else if ((last_valid_visit_type_id = this.model.get("last_valid_visit_type_id"))) {
      visitType = this.model.findValidVisitType({ id: last_valid_visit_type_id });
    }

    return this.model.set({ visit_type_id: visitType != null ? visitType.id : undefined });
  }

  handleVisitTypeIdChange() {
    this.resetSelectedVisitTypeRule();
    return this.setSlotFromCurrentVisitType();
  }

  updateSuggesterIfConditionsChanged(model) {
    if (
      model.attributesChanged(
        "ehr_appointment_slot_id",
        "appointment_slot",
        "visit_type_id",
        "last_valid_visit_type",
        "returning_patient",
        "provider",
      )
    ) {
      return this.renderSuggester();
    }
  }

  // RENDERING

  render() {
    if (!this.renderable) {
      return this;
    }

    super.render(...arguments);

    this.replaceContents(".provider-type-callout-container", this.providerTypeCallout.render().el);
    this.replaceContents(".slot-message-callout-container", this.slotMessagesCallout.render().el);
    this.renderField(".returning-patient-field", this.returningPatientField);
    this.renderField(".visit-type-field", this.visitTypeField);
    this.renderField(".age-range-field", this.ageRangeField);
    this.replaceContents(".age-range-callout-container", this.ageRangeCallout.render().el);
    this.renderField(".visit-type-rules-field", this.visitTypeRulesField);
    this.replaceContents(".no-matching-visit-type-callout-container", this.noMatchingVisitTypeCallout.el);
    this.renderField(".slot-field", this.slotField);
    this.replaceContents(".location-during-visit-field", this.locationDuringVisitField.render().el);
    this.replaceContents(
      ".not-licensed-for-location-callout-container",
      this.notLicensedForLocationCallout.render().el,
    );
    this.renderSuggester();

    return this;
  }

  // NOTE: Be sure to keep updateSuggesterIfConditionsChanged up to date with the attributes used to render
  renderSuggester() {
    if (this.loadingSuggester) {
      return;
    }

    this.loadingSuggester = true;

    return this.whenSlotSuggesterLoaded((suggester) => {
      this.loadingSuggester = false;
      this.clearContents(".slot-reselector-container");

      if (!this.shouldShowSlotSuggester()) {
        return;
      }

      this.replaceContents(".slot-reselector-container", suggester.el);

      // Updates the suggester with the slot conditions currently selected in the booking
      return suggester.setDesiredSlotConditions({
        id: this.model.get("appointment_slot")?.id,
        // Override the conditions inherited from the desired slot with the user's updated choices
        visitTypeId: this.model.get("visit_type_id"),
        matchName: this.model.get("last_valid_visit_type")?.match_name,
        patientType: this.model.get("returning_patient") ? "returning" : "new",
        resourceId: this.model.get("provider")?.id,
        resourceType: this.model.get("provider")?.type,
        startTime: this.model.get("appointment_slot")?.start_time,
      });
    });
  }

  // HELPER FUNCTIONS

  whenSlotSuggesterLoaded(fn) {
    return this.initializeSlotSuggester().then(fn);
  }

  initializeSlotSuggester() {
    return (
      this.slotSuggesterPromise ||
      (this.slotSuggesterPromise = AppointmentBooking.initializeSlotSuggester({
        variant: "outlined-elevated",
      }))
    );
  }

  getPageTitle() {
    return this.I18n().visit.page_title;
  }

  // Pick the first option for each rule when switching to a new visit type
  resetSelectedVisitTypeRule() {
    const currentVisitType = this.model.get("visit_type");

    if (currentVisitType) {
      const value = currentVisitType.rules.map((rule) => ({
        id: rule.id.toString(),
        option_id: rule.options[0].id.toString(),
      }));
      return this.model.set("visit_type_rule_selections", value);
    }
  }

  setSlotFromCurrentVisitType() {
    let slot = this.model.get("visit_type")?.matching_slot;
    if (!(slot != null ? slot.visible : undefined)) {
      slot = null;
    }
    return this.model.set("ehr_appointment_slot_id", slot != null ? slot.id : undefined);
  }

  // PRIVATE

  ageRangeRestriction() {
    if (this.model.get("in_age_range")) {
      return false;
    }

    const visitType = this.model.get("visit_type");

    if (!visitType) {
      return false;
    }

    return visitType.verify_age_range;
  }

  visitTypeRuleRestriction() {
    const availableRules = this.model.getSelectedVisitTypeRules();
    const selectedVisitTypeRuleOptions = this.model.get("visit_type_rule_selections");

    return availableRules.some((rule) => {
      const selectedOption = rule.options.find((option) =>
        selectedVisitTypeRuleOptions.findWhere({ id: rule.id.toString(), option_id: option.id.toString() }),
      );

      return !selectedOption || !selectedOption.allow_booking;
    });
  }

  shouldShowSlotSuggester() {
    return !this.slotSelected();
  }

  slotSelected() {
    return !!this.model.get("ehr_appointment_slot_id");
  }

  shouldGatherLocationDuringVisit() {
    return this.selectedVisitTypeChecksProviderLicense() && this.getLicensedLocations().length;
  }

  licenseRestriction() {
    if (!this.selectedVisitTypeChecksProviderLicense()) {
      return false;
    }

    const location = this.model.get("location_during_visit");

    if (!location) {
      return true;
    }

    const licensedLocations = this.getLicensedLocations();

    // Return true if the user will be in a location where the doctor is not licensed
    if (location && licensedLocations.length) {
      return !_.contains(licensedLocations, location);
    }
  }

  selectedVisitTypeChecksProviderLicense() {
    return this.model.get("visit_type")?.check_provider_license;
  }

  getLicensedLocations() {
    return this.model.get("provider").licensed_locations;
  }
};

AppointmentBooking.Wizard.Views.VisitStep.prototype.template = "step/visit";
AppointmentBooking.Wizard.Views.VisitStep.prototype.name = "Welcome";
AppointmentBooking.Wizard.Views.VisitStep.prototype.className = "appointment-booking-wizard-visit-step";

if (__VisitStepPrior) {
  Object.assign(AppointmentBooking.Wizard.Views.VisitStep, __VisitStepPrior);
}
